import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { usePermissions } from '../../contexts/PermissionContext';
import axios from 'axios';

/**
 * Attendance List Page
 * Displays all attendance records with filtering options
 */
const AttendanceList: React.FC = () => {
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  useEffect(() => {
    fetchAttendanceRecords();
  }, []);

  const fetchAttendanceRecords = async () => {
    try {
      const response = await axios.get('/attendance');
      setAttendanceRecords(response.data);
    } catch (error) {
      console.error('Failed to fetch attendance records:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PRESENT':
        return 'success';
      case 'ABSENT':
        return 'error';
      case 'LATE':
        return 'warning';
      case 'EXCUSED':
        return 'info';
      case 'HALF_DAY':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4">Attendance Records</Typography>
          {hasPermission('ATTENDANCE_MARK') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/attendance/create')}
            >
              Mark Attendance
            </Button>
          )}
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Marked By</TableCell>
                <TableCell>Total Students</TableCell>
                {hasPermission("ATTENDANCE_MARK") && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : attendanceRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No attendance records found
                  </TableCell>
                </TableRow>
              ) : (
                attendanceRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      {new Date(record.date).toLocaleDateString()}
                    </TableCell>

                    <TableCell>
                      {record.class?.name || "-"}
                    </TableCell>

                    <TableCell>
                      {record.section?.name || "-"}
                    </TableCell>

                    <TableCell>
                      {record.user
                        ? `${record.user.firstName} ${record.user.lastName}`
                        : "-"}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={`${record._count?.entries || 0} Students`}
                        color="primary"
                        size="small"
                      />
                    </TableCell>

                    {hasPermission("ATTENDANCE_MARK") && (
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/attendance/${record.id}`)}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))

              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </MainLayout>
  );
};

export default AttendanceList;



